// Locations — 真實資料版（Route 3, PR #89）。
// 後端：/v1/locations CRUD on location-api。
// Sites 下拉走 /v1/sites（Route 2 已上）。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus, ScanLine, RefreshCw, ServerCrash, MapPin, Trash2, ClipboardList, Clock, ArrowRight, X,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import PaginationControl from "../components/PaginationControl.js";
import { usePagination } from "../lib/usePagination.js";
import Drawer from "../components/Drawer.js";
import LocationBindingCard from "../components/LocationBindingCard.js";
import {
  listAssignmentsForLocation, type AssignmentRowWithTask,
} from "../lib/assignmentsApi.js";
// import LocationLocationPicker from "../components/LocationLocationPicker.js";   // geo UI 暫時隱藏，配合 fieldset 一起 uncomment
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import { Field } from "../components/Field.js";
import {
  listLocations,
  createLocation,
  patchLocation,
  retireLocation,
  friendlyLocationsError,
  type LocationRow,
  type LocationRisk,
  type LocationStatus,
} from "../lib/locationsApi.js";
import { listSites, type SiteRow } from "../lib/sitesApi.js";
import { humanizeRRule } from "../lib/humanRrule.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";

const STATUS_BADGE: Record<LocationStatus, string> = {
  active: "badge-success",
  retired: "badge-default",
};
const STATUS_LABEL: Record<LocationStatus, string> = {
  active: "啟用",
  retired: "已退役",
};
const RISK_BADGE: Record<LocationRisk, string> = {
  low: "badge-default",
  medium: "badge-warning",
  high: "badge-danger",
};
const RISK_LABEL: Record<LocationRisk, string> = {
  low: "低",
  medium: "中",
  high: "高",
};

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: LocationRow[]; total: number }
  | { status: "error"; message: string };

type DrawerState = null | { mode: "new" } | { mode: "edit"; cp: LocationRow };

export default function Locations(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:site") || session.super_admin === true;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editing, setEditing] = useState<DrawerState>(null);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const siteFilter = searchParams.get("site_id");
  const pag = usePagination({ urlPrefix: "cp_" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listLocations({
        tenantId: tenantParam,
        siteId: siteFilter,
        limit: pag.limit,
        offset: pag.offset,
      });
      setState({ status: "ready", rows: r.items, total: r.total });
    } catch (err) {
      setState({ status: "error", message: friendlyLocationsError(err) });
    }
  }, [tenantParam, siteFilter, pag.limit, pag.offset]);

  const loadSites = useCallback(async () => {
    try {
      const r = await listSites({ tenantId: tenantParam, status: "active", limit: 200 });
      setSites(r.items);
    } catch {
      setSites([]);
    }
  }, [tenantParam]);

  useEffect(() => {
    void load();
    void loadSites();
  }, [load, loadSites]);

  const columns: Column<LocationRow>[] = useMemo(() => [
    {
      key: "code",
      header: "代碼 / 標籤",
      primary: true,
      render: (cp) => (
        <div className="flex items-start gap-2">
          <ScanLine className="h-4 w-4 text-text-muted flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-medium font-mono text-text-primary truncate">{cp.code}</div>
            <div className="text-2xs text-text-muted truncate">{cp.label}</div>
          </div>
        </div>
      ),
      width: "30%",
    },
    {
      key: "site",
      header: "案場",
      hideOnMobile: true,
      render: (cp) => <span className="text-text-secondary">{cp.site_name ?? cp.site_id}</span>,
    },
    {
      key: "ntag",
      header: "加密標籤",
      render: (cp) =>
        cp.ntag_bound ? (
          <span className="font-mono text-2xs text-text-secondary truncate">{cp.ntag_uid}</span>
        ) : (
          <span className="badge-default">未綁定</span>
        ),
    },
    {
      key: "risk",
      header: "風險",
      render: (cp) => <span className={RISK_BADGE[cp.risk_level]}>{RISK_LABEL[cp.risk_level]}</span>,
    },
    {
      key: "status",
      header: "狀態",
      render: (cp) => <span className={STATUS_BADGE[cp.status]}>{STATUS_LABEL[cp.status]}</span>,
    },
  ], []);

  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="地點" description="案場內的地點，與加密巡邏標籤綁定，做為現場感應檢核的對應位置" />
        <TenantPickerEmptyState />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="地點"
        description="案場內的地點，與加密巡邏標籤綁定，做為現場感應檢核的對應位置"
        actions={
          <>
            <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
            </button>
            {canWrite && (
              <button data-tour-id="locations.new" onClick={() => setEditing({ mode: "new" })} className="btn-primary text-sm" disabled={sites.length === 0}>
                <Plus className="h-4 w-4" />
                <span>新增地點</span>
              </button>
            )}
          </>
        }
      />
      {state.status === "loading" && <LocationsSkeleton />}
      {state.status === "error" && (
        <EmptyState
          icon={ServerCrash}
          title="地點列表載入失敗"
          description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>}
        />
      )}
      {/* Tenant-wide empty state — no site filter active. Big CTA so a
          fresh tenant can dive in. When the operator filtered to a
          specific site, the other empty state below kicks in instead
          (no CTA, just「該案場目前沒有地點」). */}
      {state.status === "ready" && state.rows.length === 0 && !siteFilter && (
        <EmptyState
          icon={ScanLine}
          title="這個單位還沒有地點"
          description={sites.length === 0 ? "請先到「案場」頁建立至少一個案場，再回來新增地點。" : "為案場新增第一個地點，並綁定對應的加密巡邏標籤。"}
          action={canWrite && sites.length > 0 ? <button data-tour-id="locations.new" className="btn-primary text-sm" onClick={() => setEditing({ mode: "new" })}>新增地點</button> : undefined}
        />
      )}
      {state.status === "ready" && siteFilter && (() => {
        const siteName = state.rows.find((c) => c.site_id === siteFilter)?.site_name
          ?? sites.find((s) => s.id === siteFilter)?.name
          ?? siteFilter;
        return (
          <div className="mb-3 inline-flex items-center gap-2 rounded-md border border-accent/40 bg-accent-subtle px-3 py-1.5 text-xs text-text-secondary">
            <span>目前篩選案場：<span className="font-medium text-text-primary">{siteName}</span></span>
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("site_id");
                setSearchParams(next, { replace: true });
              }}
              className="inline-flex items-center text-text-muted hover:text-text-primary"
              aria-label="清除案場篩選"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })()}
      {/* Site-filtered empty state — only when a site filter is active.
          No CTA here; the tenant-wide empty state above already owns the
          first-time-setup CTA. */}
      {state.status === "ready" && state.rows.length === 0 && siteFilter && (
        <EmptyState
          icon={ScanLine}
          title="此案場目前沒有地點"
          description="可從上方「新增地點」建立第一個。"
        />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <>
          <DataTable
            rows={state.rows}
            columns={columns}
            rowKey={(c) => c.id}
            searchableText={(c) => `${c.code} ${c.label} ${c.location_hint ?? ""} ${c.site_name ?? ""}`}
            onRowClick={(c) => setEditing({ mode: "edit", cp: c })}
            serverPagination
          />
          {/* PaginationControl sits below DataTable's card; the visual
              seam is intentional (different surface meaning — table
              data above, page navigation below). */}
          <div className="card mt-3">
            <PaginationControl
              page={pag.page}
              pageSize={pag.limit}
              total={state.total}
              onPageChange={pag.setPage}
              onPageSizeChange={pag.setLimit}
            />
          </div>
        </>
      )}
      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.mode === "edit" ? `編輯 ${editing.cp.code}` : "新增地點"}
        description="代碼、所屬案場、風險等級、加密巡邏標籤綁定"
        size="lg"
      >
        {editing && (
          <LocationForm
            mode={editing.mode}
            cp={editing.mode === "edit" ? editing.cp : undefined}
            sites={sites}
            tenantId={tenantParam}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              void load();
            }}
            canRetire={canWrite}
          />
        )}
      </Drawer>
    </>
  );
}

function LocationForm({
  mode, cp, sites, tenantId, onClose, onSaved, canRetire,
}: {
  mode: "new" | "edit";
  cp?: LocationRow;
  sites: SiteRow[];
  tenantId: string | null;
  onClose: () => void;
  onSaved: () => void;
  canRetire: boolean;
}): React.ReactElement {
  const [siteId, setSiteId] = useState(cp?.site_id ?? sites[0]?.id ?? "");
  const [code, setCode] = useState(cp?.code ?? "");
  const [label, setLabel] = useState(cp?.label ?? "");
  const [hint, setHint] = useState(cp?.location_hint ?? "");
  const [risk, setRisk] = useState<LocationRisk>(cp?.risk_level ?? "low");
  const [status, setStatus] = useState<LocationStatus>(cp?.status ?? "active");
  // Geofence (Phase 2 PR H)
  const [lat, setLat] = useState<number | null>(cp?.lat ?? null);
  const [lon, setLon] = useState<number | null>(cp?.lon ?? null);
  const [radiusM, setRadiusM] = useState<number>(cp?.geofence_radius_m ?? 30);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    const body = {
      site_id: siteId,
      code: code.trim(),
      label: label.trim(),
      location_hint: hint.trim() || null,
      risk_level: risk,
      status,
      lat,
      lon,
      geofence_radius_m: radiusM,
    };
    try {
      if (mode === "new") {
        await createLocation(body, tenantId);
      } else if (cp) {
        await patchLocation(cp.id, body, tenantId);
      }
      onSaved();
    } catch (e) {
      setErr(friendlyLocationsError(e));
    } finally {
      setBusy(false);
    }
  };

  const dlg = useConfirmDialog();
  const retire = async () => {
    if (!cp) return;
    if (!(await dlg.confirm(`確定要退役「${cp.code} ${cp.label}」？退役後此地點不再用於新巡邏，但歷史資料保留。`, { destructive: true, okLabel: "退役" }))) return;
    setBusy(true);
    setErr(null);
    try {
      await retireLocation(cp.id, tenantId);
      onSaved();
    } catch (e) {
      setErr(friendlyLocationsError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Field label="所屬案場" required hint={sites.length === 0 ? "尚未建立任何案場；請先到「案場」頁建立" : undefined}>
        <select className="select" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          {sites.length === 0 && <option value="">— 尚無案場 —</option>}
          {sites.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="代碼" required hint="同一案場內需唯一，例如 CP-2F-04">
          <input data-tour-id="locations.field.code" className="input font-mono" value={code} onChange={(e) => setCode(e.target.value)} placeholder="CP-2F-04" />
        </Field>
        <Field label="顯示名稱" required>
          <input data-tour-id="locations.field.label" className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="機房後門" />
        </Field>
      </div>
      <Field label="位置補充">
        <input className="input" value={hint} onChange={(e) => setHint(e.target.value)} placeholder="冷氣機左側 1.2m 處" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="風險等級">
          <select className="select" value={risk} onChange={(e) => setRisk(e.target.value as LocationRisk)}>
            <option value="low">低</option>
            <option value="medium">中</option>
            <option value="high">高</option>
          </select>
        </Field>
        <Field label="狀態">
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as LocationStatus)}>
            <option value="active">啟用</option>
            <option value="retired">已退役</option>
          </select>
        </Field>
      </div>
      {/* 位置與 geofence UI 暫時隱藏 — NTAG424 已綁實體位置，geo 驗證冗餘。
          `lat` / `lon` / `radiusM` state 還在，既有 location 的座標 / 半徑
          會原樣 round-trip 回後端，不會被覆寫成 null。之後要開回來只要 uncomment。 */}
      {/*
      <fieldset className="rounded-md border border-border bg-bg-raised p-4 space-y-3">
        <legend className="text-sm font-semibold px-2">位置與 geofence</legend>
        <p className="text-2xs text-text-muted">
          設定後可在巡邏員 tap 時做地理驗證（Step 1 verify_geo toggle）。地圖點選 / 拖曳 marker 微調，
          或用「使用我目前位置」自動填入；半徑滑桿決定容許誤差。
        </p>
        <LocationLocationPicker
          lat={lat} lon={lon} radiusM={radiusM}
          onChange={({ lat: l, lon: g, radius_m }) => { setLat(l); setLon(g); setRadiusM(radius_m); }}
        />
      </fieldset>
      */}
      {mode === "edit" && cp && (
        <LocationBindingCard
          locationId={cp.id}
          tenantId={tenantId}
          initialUid={cp.ntag_uid ?? null}
          initialBound={!!cp.ntag_bound}
          onChange={onSaved}
        />
      )}
      {mode === "edit" && cp && (
        <LocationTasksSection locationId={cp.id} tenantId={tenantId} />
      )}
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-between gap-2">
        <div>
          {mode === "edit" && cp && canRetire && (
            <button className="btn-danger text-sm" onClick={() => void retire()} disabled={busy}>
              <Trash2 className="h-4 w-4" /> 退役
            </button>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button data-tour-id="locations.save" onClick={() => void save()} className="btn-primary text-sm" disabled={busy || !siteId || !code.trim() || !label.trim()}>
            {busy ? "儲存中…" : "儲存"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LocationsSkeleton(): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-border">
        <Skeleton className="h-9 w-72" />
      </div>
      <div className="divide-y divide-border">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <Skeleton className="h-4 w-4 rounded" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-5 w-12 hidden sm:block" />
            <Skeleton className="h-5 w-12 hidden md:block" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Reverse-lookup: list the task assignments that fire when this
 *  location is tapped. Source of truth is the same matcher matrix
 *  used at scan time, so what you see here is exactly what officers
 *  will see in the wizard. */
function LocationTasksSection({ locationId, tenantId }: {
  locationId: string; tenantId: string | null;
}): React.ReactElement {
  const [items, setItems] = useState<AssignmentRowWithTask[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    void (async () => {
      try {
        const r = await listAssignmentsForLocation(locationId, tenantId);
        if (!cancelled) setItems(r.items);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message ?? "load_failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [locationId, tenantId]);

  return (
    <fieldset className="rounded-md border border-border bg-bg-raised p-4 space-y-3">
      <legend className="text-sm font-semibold text-text-primary px-2 inline-flex items-center gap-1.5">
        <ClipboardList className="h-3.5 w-3.5" />
        感應此處會觸發的任務{items ? ` · ${items.length} 條` : ""}
      </legend>
      {loading && <Skeleton className="h-12 w-full" />}
      {err && <p className="text-2xs text-danger">載入失敗：{err}</p>}
      {items && items.length === 0 && !loading && (
        <p className="text-xs text-text-muted">
          目前沒有任務派發到此地點。巡邏員感應後只會留下到站紀錄。
          要派發任務，請到「任務」頁建立 / 編輯任務後新增派發。
        </p>
      )}
      {items && items.length > 0 && (
        <ul className="space-y-1.5">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-bg-base border border-border-subtle"
            >
              <ClipboardList className="h-3.5 w-3.5 text-accent flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{a.task_name ?? a.task_id.slice(0, 8) + "…"}</div>
                <div className="text-2xs text-text-muted truncate inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {humanizeRRule(a.rrule)}
                </div>
              </div>
              {a.status === "archived" && (
                <span className="badge-default text-2xs">已封存</span>
              )}
              <Link
                to={`/dashboard/tasks/${a.task_id}/assignments`}
                onClick={(e) => e.stopPropagation()}
                className="text-2xs text-accent hover:text-accent-hover inline-flex items-center gap-1 px-1.5"
                title="到任務的派發管理頁"
              >
                <ArrowRight className="h-3 w-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}
